import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Download, QrCode, RotateCcw } from "lucide-react";
import { createCompartmentQrImage, isZoneCompartmentName } from "../../lib/compartmentQrImage";
import { qrPayload } from "../../lib/qrPayload";
import { drawerNumber } from "../../lib/drawerBarcode";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

async function copyText(value) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (!copied) throw new Error("Kopieren wird von diesem Browser nicht unterstützt.");
}

function CompartmentQrCard({ item, testId }) {
    const isDrawer = drawerNumber(item.title) !== null;
    const payload = isDrawer ? (item.scan_number || "") : qrPayload("compartment", item.id);
    const isSign = !isDrawer && isZoneCompartmentName(item.title);
    const previewShape = isDrawer ? "min-h-16 max-w-full" : isSign ? "aspect-[210/297] max-w-40" : "aspect-[9/20] max-w-28";
    const contentLabel = isDrawer ? "Barcode-Inhalt" : "QR-Inhalt";
    const cardRef = useRef(null);
    const [shouldRender, setShouldRender] = useState(false);
    const [image, setImage] = useState(null);
    const [imageError, setImageError] = useState("");
    const [copyStatus, setCopyStatus] = useState("");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!cardRef.current || shouldRender) return undefined;
        if (!("IntersectionObserver" in window)) {
            setShouldRender(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                setShouldRender(true);
                observer.disconnect();
            },
            { rootMargin: "320px" }
        );
        observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, [shouldRender]);

    useEffect(() => {
        if (!shouldRender) return;
        setImage(null);
        setImageError("");
        try {
            setImage(createCompartmentQrImage({ id: item.id, name: item.title, scan_number: item.scan_number }));
        } catch (error) {
            setImageError(error.message || "Bild konnte nicht erzeugt werden.");
        }
    }, [item.id, item.title, item.scan_number, attempt, shouldRender]);

    const copy = async () => {
        try {
            await copyText(payload);
            setCopyStatus(`${contentLabel} kopiert.`);
        } catch (error) {
            setCopyStatus("Kopieren fehlgeschlagen. Bitte den Inhalt im Feld markieren.");
        }
    };

    return (
        <li
            ref={cardRef}
            className="flex min-w-0 flex-col gap-3 rounded-xl bg-card p-4 shadow-sm"
            data-testid={testId}
        >
            <div>
                <h3 className="text-base font-bold text-foreground">{item.title}</h3>
                {item.hint ? <p className="text-xs text-foreground/80">{item.hint}</p> : null}
                {isSign ? (
                    <p className="mt-1 text-xs font-medium text-foreground" data-testid={`${testId}-format`}>
                        A4-Aushang für Palettenplatz
                    </p>
                ) : null}
                {isDrawer ? (
                    <p className="mt-1 text-xs font-medium text-foreground" data-testid={`${testId}-format`}>
                        Schubladenetikett · 10 mm hoch · 300 dpi
                    </p>
                ) : null}
            </div>

            {image ? (
                <img
                    src={image.dataUrl}
                    alt={isDrawer
                        ? `Schubladenetikett für Fach ${item.title}: Nummer ${drawerNumber(item.title)} links und Barcode rechts`
                        : isSign
                        ? `A4-Aushang für Fach ${item.title}: Fachname und darunter QR-Code`
                        : `Etikett für Fach ${item.title}: Fachname, Pfeil nach rechts und QR-Code`}
                    className={`mx-auto w-full rounded-md bg-white object-contain shadow-sm ${previewShape}`}
                    data-testid={`${testId}-image`}
                />
            ) : imageError ? (
                <div className="rounded-md bg-destructive/10 p-4" role="alert">
                    <p className="text-sm text-destructive">{imageError}</p>
                    <Button
                        type="button"
                        variant="outline"
                        className="mt-4 h-11"
                        onClick={() => setAttempt((value) => value + 1)}
                    >
                        <RotateCcw aria-hidden="true" /> Erneut versuchen
                    </Button>
                </div>
            ) : (
                <div
                    className={`mx-auto w-full animate-pulse rounded-md bg-muted ${previewShape}`}
                    role="status"
                    aria-label="Etikett wird erzeugt"
                />
            )}

            <div className="space-y-2">
                <Label htmlFor={`${testId}-payload`}>{contentLabel}</Label>
                <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                    <Textarea
                        id={`${testId}-payload`}
                        readOnly
                        value={payload}
                        rows={2}
                        className="min-h-16 min-w-0 resize-none font-mono text-xs"
                        data-testid={`${testId}-payload`}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        className="h-11 shrink-0"
                        onClick={copy}
                        aria-label={`${contentLabel} für Fach ${item.title} kopieren`}
                        disabled={!payload}
                        data-testid={`${testId}-copy`}
                    >
                        <Copy aria-hidden="true" /> Kopieren
                    </Button>
                </div>
                {copyStatus ? (
                    <p className="text-xs text-foreground/80" role="status">
                        {copyStatus}
                    </p>
                ) : null}
            </div>

            {image ? (
                <Button
                    asChild
                    className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
                >
                    <a
                        href={image.dataUrl}
                        download={image.filename}
                        data-testid={`${testId}-download`}
                    >
                        <Download aria-hidden="true" /> PNG speichern
                    </a>
                </Button>
            ) : null}
        </li>
    );
}

export function CompartmentQrDialog({
    open,
    onClose,
    items = [],
    testId = "lager-qr-dialog",
}) {
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (open) setSearch("");
    }, [open]);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return items;
        return items.filter((item) =>
            [item.title, item.hint, item.code, item.id, item.scan_number]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query))
        );
    }, [items, search]);

    return (
        <Dialog open={open} onOpenChange={(value) => !value && onClose?.()}>
            <DialogContent
                className="flex max-h-[92vh] w-[calc(100%-2rem)] max-w-5xl flex-col overflow-hidden p-4 sm:p-6"
                data-testid={testId}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 pr-8">
                        <QrCode aria-hidden="true" /> QR-Codes für Lagerfächer
                    </DialogTitle>
                    <DialogDescription className="text-foreground/80">
                        QR-Inhalt kopieren oder das fertige Etikett als PNG für eine Drucksoftware speichern.
                        Fächer mit „Zone“ im Namen erhalten einen A4-Aushang zum Ausdrucken.
                        „Magazinschublade“ mit Nummer erhält einen 10 mm hohen Barcode-Streifen.
                    </DialogDescription>
                </DialogHeader>

                <div>
                    <Label htmlFor={`${testId}-search`}>Fächer filtern</Label>
                    <Input
                        id={`${testId}-search`}
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Name, Raum, Regal oder Code"
                        className="mt-2 h-11"
                        data-testid={`${testId}-search`}
                    />
                </div>

                <p className="text-xs text-foreground/80" role="status">
                    {filtered.length} von {items.length} Fächern angezeigt
                </p>

                <div className="min-h-0 overflow-y-auto pr-1">
                    {filtered.length ? (
                        <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {filtered.map((item) => (
                                <CompartmentQrCard
                                    key={item.id}
                                    item={item}
                                    testId={`${testId}-item-${item.id}`}
                                />
                            ))}
                        </ul>
                    ) : (
                        <div className="rounded-xl bg-card/60 p-6 text-center">
                            <p className="text-sm font-medium text-foreground">Keine passenden Fächer.</p>
                            <Button
                                type="button"
                                variant="outline"
                                className="mt-4 h-11"
                                onClick={() => setSearch("")}
                            >
                                Filter zurücksetzen
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
